
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { getDateRange } from "@/utils/dateRangeUtils";

interface SearchCount {
  query: string;
  count: number;
  avgResults: number;
  totalResults: number;
}

export const useTopSearches = (selectedDateRange: string) => {
  const dateRange = getDateRange(selectedDateRange);

  return useQuery({
    queryKey: ['top-searches', selectedDateRange],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('search_analytics')
        .select('search_query, results_count')
        .eq('interaction_type', 'search')
        .gte('created_at', dateRange.start)
        .lte('created_at', dateRange.end)
        .neq('search_query', '');
      
      if (error) throw error;
      
      // Count occurrences of each search
      const searchCounts: Record<string, SearchCount> = {};
      data.forEach(item => {
        const query = item.search_query.toLowerCase();
        if (!searchCounts[query]) {
          searchCounts[query] = { query, count: 0, avgResults: 0, totalResults: 0 };
        }
        searchCounts[query].count++;
        searchCounts[query].totalResults += item.results_count || 0;
      });
      
      // Calculate averages and sort
      return Object.values(searchCounts)
        .map(item => ({
          query: item.query,
          count: item.count,
          avgResults: Math.round(item.totalResults / item.count)
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);
    },
    refetchInterval: selectedDateRange === 'today' ? 60000 : undefined
  });
};
